import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ActiveOrderRow, useListActiveOrders } from "@/lib/api/risk/brokerage/tt/v2";
import { OrderSidePanel } from "../ttv2/side-panel/OrderSidePanel";
import { ActiveOrdersTable } from "./ActiveOrdersTable";
import { ActiveOrderSplitRow, explodeOrderToSplits } from "./types";

const FOCUS_PARAM = "focus";
const ALLOWED_ACCOUNTS = new Set(["AA066", "AA067"]);

export function TtActiveOrdersTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get(FOCUS_PARAM);

  const [selectedGuid, setSelectedGuid] = useState<string | null>(focusParam);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(focusParam != null);
  const [showAllAccounts, setShowAllAccounts] = useState<boolean>(false);

  const activeOrdersResult = useListActiveOrders();
  const orders = activeOrdersResult.data?.data?.rows ?? [];

  // Frontend filter: only AA066 / AA067 trading accounts by default. Done on
  // the array level (not via the DataTable filter) per spec. The toolbar
  // toggle lets a user opt-in to seeing every whitelisted account.
  const splitRows: ActiveOrderSplitRow[] = useMemo(() => {
    const filtered = showAllAccounts
      ? orders
      : orders.filter(
          (o: ActiveOrderRow) =>
            o.trading_account_name != null && ALLOWED_ACCOUNTS.has(o.trading_account_name)
        );
    return filtered.flatMap(explodeOrderToSplits);
  }, [orders, showAllAccounts]);

  // Keep URL in sync with selection (for shareable links).
  useEffect(() => {
    if (selectedGuid && searchParams.get(FOCUS_PARAM) !== selectedGuid) {
      const next = new URLSearchParams(searchParams);
      next.set(FOCUS_PARAM, selectedGuid);
      setSearchParams(next, { replace: true });
    }
    if (!selectedGuid && searchParams.get(FOCUS_PARAM)) {
      const next = new URLSearchParams(searchParams);
      next.delete(FOCUS_PARAM);
      setSearchParams(next, { replace: true });
    }
  }, [selectedGuid, searchParams, setSearchParams]);

  const handleOpen = useCallback((guid: string) => {
    setSelectedGuid(guid);
    setIsPanelOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedGuid(null);
  }, []);

  return (
    <div className="flex flex-col gap-3 h-full overflow-hidden">
      {/* Header strip */}
      <div className="flex flex-row items-center justify-end gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAllAccounts((v) => !v)}
        >
          {showAllAccounts ? "Show AA066/AA067 only" : "Show All"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => activeOrdersResult.refetch()}
          disabled={activeOrdersResult.isFetching}
        >
          {activeOrdersResult.isFetching ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {activeOrdersResult.isError && (
        <div className="p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-700">
          Failed to load active orders.
          {activeOrdersResult.error instanceof Error
            ? ` ${activeOrdersResult.error.message}`
            : ""}
        </div>
      )}

      {/* Body: table + optional read-only side panel */}
      <div className="flex flex-row gap-4 flex-1 min-h-0 overflow-hidden">
        <ActiveOrdersTable
          rows={splitRows}
          selectedGuid={selectedGuid}
          onOpen={handleOpen}
          isLoading={activeOrdersResult.isLoading}
        />

        {isPanelOpen && (
          <div className="w-[480px] shrink-0 border rounded-md bg-card overflow-hidden flex flex-col">
            <OrderSidePanel
              guidOrderId={selectedGuid}
              wasResolved={false}
              onClose={handleClose}
              showAttachForm={false}
              showAssociatedMetadata={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}
